import React from 'react';
import styled from 'styled-components';
import { CardRow } from '../../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../../generic/ui/Mono';
import PalletIcon from '../../../../../../generic/icons/IconLib/PalletIcon';
import HashIcon from '../../../../../../generic/icons/IconLib/HashIcon';
import UserIcon from '../../../../../../generic/icons/IconLib/UserIcon';
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon';
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon';
import PrintIcon from '../../../../../../generic/icons/IconLib/PrintIcon';
import FillGauge from '../../Components/FillGauge';
import { IPalletAzmanaItem } from '../types/palletSection';

interface Props {
    pallet: IPalletAzmanaItem;
    index: number;
    interactive: boolean;
    onOpen: () => void;
    onPrint: (palletNum: number) => void;
    isLast?: boolean;
}

const LikutPalletRow: React.FC<Props> = ({
    pallet,
    index,
    interactive,
    onOpen,
    onPrint,
    isLast = false,
}) => {
    const { Pallet_Num, CheckedBy, FillPercent, WeightNeto, Quantity } = pallet;

    const handleClick = interactive ? onOpen : undefined;
    const handleKeyDown = interactive
        ? (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen();
              }
          }
        : undefined;

    const handlePrint = (e: React.MouseEvent) => {
        e.stopPropagation();
        onPrint(Pallet_Num);
    };

    return (
        <CardRow
            $columns='7.5rem minmax(0, 1fr) auto auto'
            $mobileColumns='auto minmax(0, 1fr) auto auto'
            $isLast={isLast}
            $interactive={interactive}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
        >
            <CardRow.Cell>
                <CardRow.Stack $gap='0.125rem'>
                    <CardRow.Line $gap='0.375rem'>
                        <IdIcon>
                            <PalletIcon color='currentColor' size={16} />
                        </IdIcon>
                        <Id>{Pallet_Num}</Id>
                    </CardRow.Line>
                    <CardRow.Line $gap='0.25rem'>
                        <SubText>משטח</SubText>
                        <SubIcon>
                            <HashIcon color='currentColor' size={12} />
                        </SubIcon>
                        <SubText>{index + 1}</SubText>
                    </CardRow.Line>
                    {CheckedBy?.trim() ? (
                        <CardRow.Line $gap='0.25rem'>
                            <NameIcon>
                                <UserIcon color='currentColor' size={12} />
                            </NameIcon>
                            <SubText>{CheckedBy.trim()}</SubText>
                        </CardRow.Line>
                    ) : null}
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell>
                <CardRow.Stack $gap='0.125rem'>
                    <MetricLine>
                        <MetaIcon>
                            <ScaleIcon color='currentColor' size={14} />
                        </MetaIcon>
                        <MetricText>{WeightNeto?.toFixed(2)}</MetricText>
                    </MetricLine>
                    <MetricLine>
                        <MetaIcon>
                            <PackageBoxIcon color='currentColor' size={14} />
                        </MetaIcon>
                        <MetricText>{Quantity}</MetricText>
                    </MetricLine>
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell $align='center'>
                <FillGauge percent={FillPercent} />
            </CardRow.Cell>

            <CardRow.Cell $align='center'>
                <PrintBtn type='button' onClick={handlePrint} aria-label='הדפס משטח'>
                    <PrintIcon size={16} color='currentColor' />
                </PrintBtn>
            </CardRow.Cell>
        </CardRow>
    );
};

export default LikutPalletRow;

const IdIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink2};
    flex-shrink: 0;
`;

const NameIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const SubIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink4};
    flex-shrink: 0;
`;

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const SubText = styled.span`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink4};
    font-family: ${({ theme }) => theme.fonts.sans};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const MetaIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const MetricLine = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
`;

const MetricText = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const PrintBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 2rem;
    min-width: 2rem;
    padding: 0 0.5rem;
    border-radius: 0.5rem;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border: 1px solid ${({ theme }) => theme.colors.accent};
    cursor: pointer;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accentDeep};
        border-color: ${({ theme }) => theme.colors.accentDeep};
    }

    &:active {
        transform: translateY(1px);
    }
`;
